import {
  ArrowTopRightOnSquareIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { dashboardV2Client } from '../api/client';
import { ShiftDetailInvite, ShiftDetailWorker } from '../api/types';

import Avatar from './Avatar';

import useAuth from '@/hooks/useAuth';
import { trackDashboardV2ActionTaken } from '@/util/vexo';

/**
 * "Invited workers" — the list behind an activity row's "4 workers"
 * (GRAVY-10345, Figma 39493:117042; completed by GRAVY-10350, corrected by
 * GRAVY-10355).
 *
 * The activity feed says a group was invited or reminded; this answers WHO,
 * without sending the admin off to another screen and losing the shift they
 * were reading.
 *
 * ## What the rows are — and why that changed
 *
 * They are the panel's LIVE invites: the shift's outstanding `JobOffer`s.
 *
 * They used to be the ACTIVITY EVENT's recorded `worker_ids`, which is a
 * snapshot of one moment. Order creation sends a bulk invite and logs one event
 * carrying those ids; every worker invited afterwards, one at a time, is a
 * separate offer that event never heard of. So the list showed the original
 * batch and silently omitted everyone since — GRAVY-10355 item 2.
 *
 * The activity row's own label ("invited 4 workers") stays the AUDIT count: it
 * reports what happened at that moment and must not drift. This list answers a
 * different question — who is invited on this shift *now* — which is why the
 * two numbers can legitimately differ, and why the list says so in a caption
 * rather than leaving the reader to notice.
 *
 * `names`/`workerIds` from the event survive as the fallback for a payload with
 * no `invites` at all (an older API): the event's frozen list is then the only
 * answer available, and showing it beats showing nothing.
 */

type Props = {
  /** The event's recorded names — the fallback when the payload has no invites. */
  names: string[];
  workerIds: number[];
  /**
   * The shift's outstanding invitations, and the list's real source. `undefined`
   * means the payload carried none of this (an older API); an empty ARRAY means
   * nobody is invited right now, which is a fact worth stating.
   */
  invites?: ShiftDetailInvite[];
  /** The shift's roster, for the Hire guard. */
  roster?: ShiftDetailWorker[];
  /** Needed to hire; when absent the action is not offered. */
  jobId?: number | null;
  /** Opens a worker's profile, the same action a linked name in a row performs. */
  onSelectWorker?: (workerId: number) => void;
  /** Refetches the shift after a hire, so the roster and counts catch up. */
  onHired?: () => void;
  onClose: () => void;
};

/** One rendered row, from whichever source is available. */
type Row = {
  workerId: number | null;
  name: string;
  avatarUrl?: string | null;
  status?: string | null;
  statusLabel?: string | null;
  /**
   * The server's verdict on whether this worker can be hired from here, and its
   * reason (GRAVY-10357). `undefined` where the payload carries neither — an
   * older API — which is what the local fallback below is for.
   */
  hireEligible?: boolean | null;
  hireBlockedLabel?: string | null;
};

const InvitedWorkersModal = ({
  names,
  workerIds,
  invites,
  roster,
  jobId,
  onSelectWorker,
  onHired,
  onClose,
}: Props) => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [menuUp, setMenuUp] = useState(false);
  const [hiring, setHiring] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  const customerAdmin = !!currentAdmin && isCurrentCustomerAdmin(currentAdmin);

  useEffect(() => {
    closeRef.current?.focus();
    const opener = openerRef.current as HTMLElement | null;
    return () => opener?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      // Escape closes the MENU first when one is open, so it does not also
      // dismiss the list the admin is reading.
      if (menuFor != null) setMenuFor(null);
      else onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [menuFor, onClose]);

  /**
   * Open the menu UPWARD when it would not fit below (GRAVY-10355 item 4).
   *
   * The list scrolls (`overflow-y: auto`), so a menu hanging below the last
   * row is clipped by that scroller — it looked "slightly off" but was really
   * cut in half. Measured rather than assumed from the row index: how much room
   * is left depends on the modal's height and how far the list is scrolled,
   * neither of which the index knows.
   *
   * Runs in a layout effect so the flip happens before paint; the menu never
   * appears in the wrong place first.
   */
  useLayoutEffect(() => {
    if (menuFor == null) {
      setMenuUp(false);
      return;
    }
    const menu = menuRef.current;
    const list = listRef.current;
    if (!menu || !list) return;

    const menuBox = menu.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();
    const overflowsBelow = menuBox.bottom > listBox.bottom;
    // Only flip if there is actually room above — otherwise the fix moves the
    // clipping to the other end.
    const roomAbove = menuBox.top - menuBox.height - listBox.top;
    setMenuUp(overflowsBelow && roomAbove > 0);
  }, [menuFor]);

  const byWorker = new Map(
    (roster ?? [])
      .filter((w) => w.worker_id != null)
      .map((w) => [w.worker_id as number, w]),
  );

  // Live invites where the payload has them; the event's frozen names only when
  // it does not (see the note above).
  const rows: Row[] = invites
    ? invites.map((i) => ({
        workerId: i.worker_id ?? null,
        name: i.name || 'Unknown worker',
        avatarUrl: i.avatar_url,
        status: i.status,
        statusLabel: i.status_label,
        hireEligible: i.hire_eligible,
        hireBlockedLabel: i.hire_blocked_label,
      }))
    : names.map((name, i) => ({
        workerId: workerIds[i] ?? null,
        name,
      }));

  /**
   * Why this worker cannot be hired from here — null when they can.
   *
   * ## The SERVER decides (GRAVY-10357)
   *
   * `hire_eligible` / `hire_blocked_label` come from `Dashboard::HirePolicy`,
   * which mirrors every refusal of the hire path that is knowable before the
   * click. Ops asked for the button to be off for ALL ineligible workers, and two
   * of the three states they named cannot be answered here at all: a schedule
   * conflict needs the worker's other assignments across the agency, and whether
   * the job is still available needs `cancelled_at` and the two-week hiring
   * window. Guessing locally would be a second, weaker copy of a rule that
   * already exists.
   *
   * The label is the server's too, so a reason added there cannot ship as a
   * disabled control with nothing to explain it.
   *
   * ## The local fallback
   *
   * Kept for a payload carrying no verdict (an older API), where these three are
   * the ones this component can still answer from the roster it was handed:
   *
   *   already on the roster  -> Reachable even from an "Invited" row: a direct
   *                             hire leaves the offer outstanding, so the same
   *                             worker is both.
   *   dropped this shift     -> `worker_already_dropped_job` (a force-hire
   *                             override exists, but not on this surface).
   *   declined the invite    -> `worker_offer_rejected`, but ONLY for a customer
   *                             admin. A tenant admin may still direct hire a
   *                             decliner, and disabling it for them would remove
   *                             a real recovery action over a rule that does not
   *                             apply to them.
   *
   * An EXPIRED invite is not blocked by either path: nothing server-side refuses
   * it, and a lapsed invitation is the most ordinary reason to reach for Hire.
   *
   * Either way the hire endpoint stays the authority — this only avoids offering
   * an action whose refusal is already knowable, and the modal still surfaces the
   * service's own message when one gets through.
   */
  const hireBlockedReason = (row: Row): string | null => {
    // A row the server answered for: trust it in BOTH directions. Falling back
    // to the local rules for an eligible row would let the weaker copy overrule
    // the stronger one — e.g. keeping a decliner disabled after the server has
    // said this admin may hire them.
    if (row.hireEligible != null)
      return row.hireEligible ? null : (row.hireBlockedLabel ?? 'Cannot hire');

    const onRoster = row.workerId != null ? byWorker.get(row.workerId) : null;
    if (onRoster?.dropped) return 'Dropped this shift';
    if (onRoster && onRoster.hired && !onRoster.removed) return 'Already hired';
    if (row.status === 'declined' && customerAdmin)
      return 'Declined the invite';
    return null;
  };

  /** The roster state, where it CONTRADICTS the invite pill. */
  const rosterNote = (row: Row): string | null => {
    const onRoster = row.workerId != null ? byWorker.get(row.workerId) : null;
    if (!onRoster) return null;
    if (onRoster.dropped) return 'Dropped';
    // An outstanding invite for someone already working the shift would
    // otherwise read as "Invited" and nothing else.
    if (onRoster.hired && !onRoster.removed) return 'Hired';
    return null;
  };

  const hire = async (workerId: number) => {
    if (!jobId) return;
    setMenuFor(null);
    setError(null);
    setHiring(workerId);
    try {
      await dashboardV2Client.hireWorker(jobId, workerId);
      // Reported AFTER the call resolves, never before: `dashboard.actions`
      // and the `action_rate` derived from it are meant to measure work that
      // actually happened. A hire the service refused (withholdings, an
      // overlap, a past job) would otherwise count as task completion.
      trackDashboardV2ActionTaken({
        action: 'hire_worker',
        tab: 'calendar',
        worker_id: workerId,
      });
      onHired?.();
      onClose();
    } catch (e) {
      // The service's own message where there is one — it explains WHY the hire
      // was refused (withholdings, an overlap, a past job), which a generic
      // failure would throw away.
      const message =
        (e as { response?: { data?: { errors?: string[] } } })?.response?.data
          ?.errors?.[0] ?? 'Could not hire this worker. Please try again.';
      setError(message);
    } finally {
      setHiring(null);
    }
  };

  return (
    <div
      className="dashv2-actmodal-overlay dashv2-invitedmodal-overlay"
      data-testid="invitedWorkersModal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dashv2-actmodal dashv2-invitedmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="dashv2-actmodal-head">
          <h2 id={titleId} className="dashv2-actmodal-title">
            Invited workers
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="dashv2-panel-close"
            aria-label="Close invited workers"
            onClick={onClose}
          >
            <XMarkIcon />
          </button>
        </div>

        {/* Says which question the list answers, so a count that differs from
            the activity row's reads as the two different facts they are rather
            than as a bug. Only when the list IS the live one. */}
        {invites && (
          <p
            className="dashv2-invitedcaption"
            data-testid="invitedWorkersCaption"
          >
            Invited on this shift now
          </p>
        )}

        {error && (
          <p className="dashv2-actmodal-state" role="alert">
            {error}
          </p>
        )}

        <div className="dashv2-invitedlist" ref={listRef}>
          {rows.length === 0 ? (
            <p className="dashv2-actmodal-state">
              {invites
                ? 'Nobody is invited on this shift right now.'
                : 'The workers on this event are no longer available.'}
            </p>
          ) : (
            rows.map((row, i) => {
              const id = row.workerId;
              const blocked = hireBlockedReason(row);
              const note = rosterNote(row);
              return (
                <div
                  className="dashv2-invitedrow"
                  data-testid="invitedWorkerRow"
                  key={`${id ?? row.name}-${i}`}
                >
                  <Avatar name={row.name} src={row.avatarUrl ?? null} />

                  <div className="dashv2-invitedrow-main">
                    {id != null && onSelectWorker ? (
                      <button
                        type="button"
                        className="dashv2-panel-actlink"
                        onClick={() => onSelectWorker(id)}
                      >
                        {row.name}
                      </button>
                    ) : (
                      <span className="dashv2-invitedname">{row.name}</span>
                    )}
                    <span className="dashv2-invitedchips">
                      {/* Their state on THIS shift — Invited, Declined,
                          Expired. Rendered from the panel's own invite row
                          rather than assumed, so a decliner is not listed as
                          merely invited. */}
                      {row.statusLabel && (
                        <span
                          className={`dashv2-invitedstatus is-${row.status}`}
                          data-testid="invitedWorkerStatus"
                        >
                          {row.statusLabel}
                        </span>
                      )}
                      {note && (
                        <span
                          className="dashv2-invitedstatus is-roster"
                          data-testid="invitedWorkerRosterNote"
                        >
                          {note}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="dashv2-invitedrow-actions">
                    {id != null && onSelectWorker && (
                      <button
                        type="button"
                        className="dashv2-invitediconbtn"
                        aria-label={`View profile for ${row.name}`}
                        onClick={() => onSelectWorker(id)}
                      >
                        <ArrowTopRightOnSquareIcon />
                      </button>
                    )}

                    {id != null && jobId != null && (
                      <div className="dashv2-invitedmenu-wrap">
                        <button
                          type="button"
                          className="dashv2-invitediconbtn"
                          aria-label={`More actions for ${row.name}`}
                          aria-expanded={menuFor === id}
                          aria-haspopup="menu"
                          disabled={hiring != null}
                          onClick={() => setMenuFor(menuFor === id ? null : id)}
                        >
                          <EllipsisVerticalIcon />
                        </button>

                        {menuFor === id && (
                          <div
                            ref={menuRef}
                            className="dashv2-invitedmenu"
                            role="menu"
                            data-placement={menuUp ? 'up' : 'down'}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              className="dashv2-invitedmenu-item"
                              disabled={hiring === id || blocked != null}
                              // The reason, not just a dead control: an admin
                              // who cannot hire from here needs to know why.
                              title={blocked ?? undefined}
                              onClick={() => hire(id)}
                            >
                              {hiring === id ? 'Hiring…' : 'Hire worker'}
                            </button>
                            {blocked && (
                              <p
                                className="dashv2-invitedmenu-note"
                                data-testid="invitedHireBlocked"
                              >
                                {blocked}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitedWorkersModal;
